import { ArtButton } from "@art/shared-ui";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { updatePageState } from "../../store";
import AppSwitcher from "../header/app-switch/app-switcher";
import styles from "./application-unavailable.module.scss";

type Application = "AGENT_LAB" | "LIVE_CONNECT" | "INTERFACE" | "DATALAKE";

const details = {
  AGENT_LAB: {
    name: "Agent Lab",
    logo: "/assets/images/agent-lab-short.svg",
    className: styles.agentLab,
  },
  LIVE_CONNECT: {
    name: "Live Connect",
    logo: "/assets/images/live-connect-short.svg",
    className: styles.liveConnect,
  },
  INTERFACE: {
    name: "App Studio",
    logo: "/assets/images/A-Realtime-Tech-short-logo.svg",
    className: styles.appStudio,
  },
  DATALAKE: {
    name: "Datalake",
    logo: "/assets/images/datalake-short.svg?v=cosmic-memory",
    className: styles.datalake,
  },
};

export default function ApplicationUnavailable({
  application,
  hasApplicationAccess,
  permissionError = false,
  onRetry,
}: {
  application: Application;
  hasApplicationAccess: boolean;
  permissionError?: boolean;
  onRetry?: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const product = details[application];
  useEffect(() => {
    dispatch(updatePageState({ page_title: product.name, force_fullscreen: false }));
  }, [dispatch, product.name]);
  const title = permissionError
    ? "We could not verify your access"
    : "You do not have access to this application";
  const message = permissionError
    ? "The permission service is unavailable right now. Retry before asking your administrator to change your role."
    : hasApplicationAccess
      ? `Your role can use ${product.name}, but it does not include dashboard insights. Choose another section or ask your tenant administrator for dashboard access.`
      : `Ask an administrator of this tenant to provide access to ${product.name}. You can still explore the other applications available here.`;

  return (
    <section className={`${styles.shell} ${product.className}`} aria-labelledby="application-access-title">
      <div className={styles.artwork} aria-hidden="true"><span /><span /><span /></div>
      <div className={styles.content}>
        <div className={styles.logo}><img src={product.logo} alt="" /></div>
        <p className={styles.eyebrow}>{product.name} · Access</p>
        <h1 id="application-access-title">{title}</h1>
        <p className={styles.description}>{message}</p>
        <div className={styles.actions}>
          {permissionError && onRetry && (
            <ArtButton buttonType="button" label="Retry access check" dataQa="retry-application-access" onClick={() => void onRetry()} />
          )}
          <AppSwitcher renderTrigger={({ open }) => (
            <ArtButton buttonType="button" label="Switch application" variant={permissionError ? "outlined" : "contained"} dataQa="switch-application-from-access-page" onClick={open} />
          )} />
          <ArtButton
            buttonType="button"
            label="My profile"
            variant="outlined"
            dataQa="open-profile-from-access-page"
            onClick={() => navigate("/account-settings/general/personal-profile")}
          />
        </div>
        {!permissionError && <p className={styles.note}>Access is managed by your tenant administrator. Switching applications does not change your permissions.</p>}
      </div>
    </section>
  );
}
